import React from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import moment from "moment-timezone";
import { useStyles } from "./Receipts.styles";
import ReceiptBody from "./ReceiptBody";
import ReceiptHeaderFooter from "./ReceiptHeaderFooter";
import GenericError from "../GenericError";

/**
 * Formats a payment date/time for the panel header. Returns an empty string
 * when the value is absent or invalid.
 *
 * @param {string|Date} dt The payment date/time (facility timezone).
 * @returns {string} The formatted date/time or an empty string.
 */
const formatDateTime = (dt) => {
  if (dt === null || dt === undefined || dt === "") {
    return "";
  }
  const parsed = moment(dt);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY HH:mm") : "";
};

/**
 * A single collapsible receipt panel for one Payment_Transaction.
 *
 * Rendered as a MUI v4 Accordion collapsed by default. The summary shows the
 * receipt title ("Receipt" or "Receipt - Lost Ticket") and the payment
 * date/time (Requirements 2.3-2.6). When expanded, the details render the
 * resolved header, the itemized ReceiptBody, then the resolved footer
 * (Requirements 2.7, 3.1, 3.2). When the receipt's configuration could not be
 * resolved (`configResolved === false`), a receipt-level error is shown in
 * place of the body (Requirement 10.6).
 *
 * @param {object} props Component props.
 * @param {object} props.receipt The receipt view model for this transaction.
 */
const ReceiptPanel = ({ receipt }) => {
  const classes = useStyles();

  if (!receipt) {
    return null;
  }

  const { title, paidTime, headerText, footerText, configResolved } = receipt;
  const formattedPaidTime = formatDateTime(paidTime);

  return (
    <Accordion
      defaultExpanded={false}
      data-testid="receipt-panel"
      className={clsx(classes.panel, "receipt-panel")}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="receipt-panel-content"
        data-testid="receipt-panel-summary"
        className="receipt-panel-summary"
      >
        <div className={clsx(classes.summaryContent, "receipt-panel-summary-content")}>
          <Typography
            className={clsx(classes.summaryTitle, "receipt-panel-title")}
            data-testid="receipt-panel-title"
          >
            {title}
          </Typography>
          <Typography
            className={clsx(classes.summaryDateTime, "receipt-panel-datetime")}
            data-testid="receipt-panel-datetime"
          >
            {formattedPaidTime}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className="receipt-panel-details">
        <div className={clsx(classes.detailsContainer, "receipt-panel-details-container")}>
          {configResolved === false ? (
            <div className="receipt-panel-config-error">
              <GenericError
                message={["Unable to load this receipt."]}
                headerMessage="Receipt unavailable"
              />
            </div>
          ) : (
            <>
              <ReceiptHeaderFooter text={headerText} variant="header" />
              <ReceiptBody receipt={receipt} />
              <ReceiptHeaderFooter text={footerText} variant="footer" />
            </>
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

ReceiptPanel.propTypes = {
  receipt: PropTypes.shape({
    title: PropTypes.string,
    paidTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    headerText: PropTypes.string,
    footerText: PropTypes.string,
    configResolved: PropTypes.bool,
  }),
};

export default ReceiptPanel;
